import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DateEx } from '../../../functions/date-ex';
import { EmailRecipient } from '../../../models/api/reports/email-recipient';
import { Action } from '../../../models/core/action';
import { ReportsService } from '../../../services/reports/reports.service';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { ActionButtonComponent } from '../../core/buttons/action-button.component';
import { DatePickerComponent } from '../../core/controls/date-picker.component';
import { InputComponent } from '../../core/controls/input.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { MenuComponent } from '../../core/panels/menu.component';
import { ColumnComponent } from '../../core/table/column.component';
import { TableComponent } from '../../core/table/table.component';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';
import { ValidatorsEx } from '../../../validators/validators-ex';

@Component({
  selector: 'app-email-recipients',
  imports: [
    CommonModule,
    ColumnComponent,
    TableComponent,
    MenuComponent,
    ActionButtonComponent,
    ActionRowComponent,
    ButtonComponent,
    ReactiveFormsModule,
    InputComponent,
    DatePickerComponent,
  ],
  template: `
    @if (editMode === 'view') {
      <app-action-row>
        <app-button
          label="Add recipient"
          left1
          emphasis="Low"
          (click)="showAddForm()"
        />
      </app-action-row>
    } @else {
      <form class="tw-pt-2 tw-pb-8" [formGroup]="fg">
        <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-gap-0">
          <app-input
            class="tw-w-full sm:tw-grow"
            [control]="fg.controls.email"
            label="Email address"
          />
          <app-date-picker
            class="tw-w-full sm:tw-min-w-[200px]"
            [showTime]="true"
            [minDate]="expiresMinDate"
            [control]="fg.controls.expires"
            label="Expires"
          />
        </div>
        <app-action-row>
          <app-button
            [label]="editMode === 'add' ? 'Save' : 'Update'"
            [disabled]="!fg.valid"
            (click)="save()"
            right1
          />
          <app-button label="Cancel" emphasis="Low" (click)="cancel()" right2 />
        </app-action-row>
      </form>
    }

    <app-table
      [dataSource]="emailRecipients"
      recordLabel="Email recipient"
      scrollHeight="800px"
      [showToolbar]="false"
      [stripedRows]="false"
      [loading]="loading"
    >
      <app-column name="email" label="Email address" />
      <app-column name="expireDate" label="Expires" format="dt" />
      <app-column name="actions" label="Actions" classes="">
        <ng-template let-data>
          <app-action-button
            [menu]="rowMenu"
            [row]="data"
            (click)="toggleMenu(rowMenu, $event, data)"
          />
        </ng-template>
      </app-column>
    </app-table>
    <app-menu [items]="rowActions" #rowMenu (click)="menuClick($event)" />
    <div class="tw-text-sm tw-italic tw-p-4">
      I acknowledge email recipients are authorized to receive client reports
      from OmniInsights. I have verified the accuracy of the email recipients
      listed. Any changes to the report structure will require me to reaffirm
      the accuracy of the intended recipients.
    </div>
  `,
  styles: ``,
})
export class EmailRecipientsComponent extends BaseComponent implements OnInit {
  private readonly reportsService = inject(ReportsService);

  @Input()
  archiveRequestId: string;

  @Input()
  requestType: 'Completed' | 'Scheduled' = 'Completed';

  emailRecipients: EmailRecipient[] = [];
  loading = false;
  editMode: EditMode = 'view';
  currentRow: EmailRecipient | null = null;

  expiresMinDate = (() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  })();

  fg = new FormGroup({
    email: new FormControl<string>(null, [
      Validators.email,
      Validators.required,
      Validators.pattern(/^[^@\s]+@[^@\s]+\.[^@\s]+$/),
    ]),
    expires: new FormControl<Date>(null, [
      Validators.required,
      ValidatorsEx.dateNotPast(),
    ]),
  });

  rowActions: Action[] = [
    { label: 'Edit', key: 'edit' },
    { label: 'Delete', key: 'delete' },
  ];

  @HandleLoadingState()
  async ngOnInit() {
    await this.loadRecipients();
  }

  @HandleLoadingState()
  private async loadRecipients(refreshCache = false) {
    this.emailRecipients = await this.api.emailRecipient.get(
      this.archiveRequestId,
      { refreshCache },
    );
  }

  showAddForm() {
    this.editMode = 'add';
    this.currentRow = null;
    this.fg.reset();
    this.fg.controls.expires.setValue(DateEx.addDays(120, new Date()));
  }

  showEditForm(recipient: EmailRecipient) {
    this.editMode = 'edit';
    this.currentRow = recipient;
    this.fg.reset();
    this.fg.controls.email.setValue(recipient.email);
    this.fg.controls.expires.setValue(
      recipient.expireDate ? new Date(recipient.expireDate) : null,
    );
  }

  cancel() {
    this.editMode = 'view';
    this.currentRow = null;
    this.fg.reset();
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async save() {
    if (!this.fg.valid) {
      return;
    }

    const email = this.fg.controls.email.value?.trim();
    const expires = this.fg.controls.expires.value;

    if (this.reportsService.emailRecipientAcknowledged) {
      // Already acknowledged this session - proceed directly
      await this.performSave(email, expires);
    } else {
      // Show acknowledgement popup
      this.confirm.show({
        label: 'Acknowledgement',
        message:
          'I acknowledge email recipients are authorized to receive client reports from OmniInsights. I have verified the accuracy of the email recipients listed. Any changes to the report structure will require me to reaffirm the accuracy of the intended recipients.',
        yesLabel: 'Accept',
        noLabel: 'Decline',
        width: '35rem',
        data: { email, expires },
        confirm: async (data: { email: string; expires: Date }) => {
          this.reportsService.emailRecipientAcknowledged = true;
          await this.performSave(data.email, data.expires);
        },
      });
    }
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  private async performSave(email: string, expires: Date) {
    if (this.editMode === 'add') {
      await this.addRecipient(email, expires);
    } else {
      await this.updateRecipient(email, expires);
    }
  }

  @HandleLoadingState()
  private async addRecipient(email: string, expiryDate?: Date) {
    const emailRecipient: EmailRecipient = {
      id: 0,
      email: email,
      link: '',
      ownerEmail: false,
      expireDate: expiryDate,
      action: 'ADD',
      proposedExpireDate: expiryDate,
    };

    await this.api.emailRecipient.add(
      emailRecipient,
      this.requestType,
      this.archiveRequestId,
    );

    this.cancel();
    await this.loadRecipients(true);
  }

  @HandleLoadingState()
  private async updateRecipient(email: string, expiryDate?: Date) {
    if (!this.currentRow) {
      return;
    }

    const emailRecipient: EmailRecipient = {
      id: this.currentRow.id,
      email: email,
      link: this.currentRow.link,
      ownerEmail: false,
      expireDate: expiryDate,
      action: 'UPDATE',
      proposedExpireDate: expiryDate,
    };

    await this.api.emailRecipient.update(
      emailRecipient,
      this.requestType,
      this.archiveRequestId,
    );

    this.cancel();
    await this.loadRecipients(true);
  }

  @HandleLoadingState()
  private async deleteRecipient(recipient: EmailRecipient) {
    const emailRecipient: EmailRecipient = {
      id: recipient.id,
      email: recipient.email,
      link: recipient.link,
      ownerEmail: false,
      expireDate: recipient.expireDate,
      action: 'DELETE',
      proposedExpireDate: recipient.proposedExpireDate,
    };

    await this.api.emailRecipient.delete(
      emailRecipient,
      this.requestType,
      this.archiveRequestId,
    );

    await this.loadRecipients(true);
  }

  toggleMenu(rowMenu: MenuComponent, event: Event, data: EmailRecipient) {
    this.currentRow = data;
    rowMenu.toggle(event);
  }

  menuClick(action: Action) {
    if (!this.currentRow) {
      return;
    }

    if (action.key === 'edit') {
      this.showEditForm(this.currentRow);
    } else if (action.key === 'delete') {
      this.deleteRecipient(this.currentRow);
    }
  }
}

type EditMode = 'add' | 'edit' | 'view';
